import styled from 'styled-components';
import chroma from 'chroma-js';
import { media } from '~/styles';
import academyStyles from '~/components/academies/common/academy-styles';

export const DEFAULT_CUSTOM_LOGO_HEIGHT = 30;

export const AcademyNavigation = styled.div<{
  noBannerContent?: boolean;
  linkColor?: string;
  linkHoverColor?: string;
  isLoggedIn?: boolean;
  searchVisible?: boolean;
  mobileNavVisible?: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: calc(100% - 8rem);
  margin: 0 4rem;

  ${media.mobile} {
    margin-left: 2rem;
    margin-right: 2rem;
    width: calc(100% - 4rem);
  }

  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  height: ${academyStyles.header.headerHeight}px;
  ${({ noBannerContent }) => noBannerContent && `height: ${academyStyles.header.headerShorterHeight}px;`}
  z-index: 15;

  .academy-navigation-content-wrapper {
    width: 100%;
    max-width: ${academyStyles.common.maxWidth}px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    padding-top: ${academyStyles.header.headerHeight / 2 - DEFAULT_CUSTOM_LOGO_HEIGHT / 2}px;
  }

  img {
    height: ${DEFAULT_CUSTOM_LOGO_HEIGHT}px;
    width: auto;
    margin-right: 75px;
  }

  a.academy-logo-link {
    img {
      float: left;
      color: ${({ linkColor }) => linkColor} !important;
    }

    &:after {
      content: '';
      display: table;
      border-collapse: collapse;
      clear: both;
    }
  }

  ul {
    padding: 0;
    list-style: none none;

    li {
      display: inline;

      &.mobile-login-link {
        display: none;
      }

      a {
        display: inline-block;
        margin: 0 20px;
        transition: all 0.3s ease-in-out;
        font-size: 1rem;
        color: ${({ linkColor }) => linkColor};

        &:hover,
        &.active {
          text-decoration: none;
          color: ${({ linkHoverColor }) => linkHoverColor};
        }
      }
    }
  }

  &.no-custom-logo {
    ul {
      li:first-child {
        a {
          margin-left: 0;
        }
      }
    }
  }

  .academy-navigation-user-avatar {
    margin-left: 15px;
    ${({ searchVisible }) => searchVisible && ' display: none;'};
  }

  .fa-bars,
  .fa-times {
    display: none;
    color: #000;
    cursor: pointer;
    width: 40px;
    height: 40px;
    background-color: #fff;
    box-shadow: 0 3px 25px rgba(161, 179, 200, 0.23);
    position: fixed;
    line-height: 40px;
    text-align: center;
    right: 2rem;
    top: 35px;
    z-index: 15;

    ${({ isLoggedIn }) => isLoggedIn && 'top: 40px;'};
  }

  ${media.mobile} {
    z-index: 99999;

    img {
      margin-right: 0;
      height: ${DEFAULT_CUSTOM_LOGO_HEIGHT}px !important;
    }

    .fa-bars {
      display: ${({ mobileNavVisible }) => (mobileNavVisible ? 'none' : 'block')};
      top: ${({ searchVisible }) => searchVisible && '30px'};
    }

    .fa-times {
      display: ${({ mobileNavVisible }) => (mobileNavVisible ? 'block' : 'none')};
      ${({ searchVisible }) =>
        searchVisible &&
        `
        top: 30px;
        background-color: transparent;
        box-shadow: none;
      `};
    }

    .academy-nav {
      position: fixed;
      top: 0;
      left: 100%;
      ${({ mobileNavVisible }) => mobileNavVisible && 'left: 0;'}
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.75);
      transition: all 0.3s ease-in-out;
      z-index: 10;

      ul {
        position: absolute;
        top: ${academyStyles.header.headerHeight}px;
        width: 100%;

        li {
          display: block;

          a {
            font-size: 1.5rem;
            display: block;
            text-align: right;
            margin: 0 2rem;
            /* If the contrast between the black backdrop
             and the link color is below the recommended 4.5,
             we fallback to white so the links are still readable.
            */
            color: ${({ linkColor }) => (chroma.contrast('#000', linkColor) > 4.5 ? linkColor : 'white')};
          }

          cursor: pointer;
        }
      }
    }

    .academy-navigation-content-wrapper {
      .logout-button {
        display: none;
      }
    }
  }

  @media print {
    display: none;
  }
`;

export const AcademyNavigationContentWrapper = styled.div<{ $hasNavOptions?: boolean }>`
  ${media.mobile} {
    ${({ $hasNavOptions }) => $hasNavOptions && 'padding-right: 55px;'}
  }
`;

export const CustomLogo = styled.img<{ logoHeight?: number }>`
  height: ${({ logoHeight }) => logoHeight || DEFAULT_CUSTOM_LOGO_HEIGHT}px !important;
`;

export const BackToLinkSpacer = styled.div`
  margin-left: auto;
`;

export const BackToLink = styled.a<{ linkColor?: string; linkHoverColor?: string }>`
  margin-left: auto;
  display: inline-block;
  text-decoration: none;
  font-size: 1rem;
  transition: all 0.3s ease-in-out;
  color: ${({ linkColor }) => linkColor} !important;
  &:hover {
    text-decoration: none;
    color: ${({ linkHoverColor }) => linkHoverColor} !important;
  }
  i {
    margin-left: 5px;
  }
  ${media.mobile} {
    span {
      display: none;
    }
    i {
      margin: 0;
    }
  }
`;

export const LogOutButton = styled.a<{ color: string; hoverColor: string }>`
  margin: 0 15px;
  border: solid 1px ${({ color }) => color};
  color: ${({ color }) => color} !important;
  border-radius: 7px;
  line-height: 2;
  padding: 0 25px;
  transition: all 0.3s ease-in-out;
  text-align: center;
  flex: 0 0 75px;
  &:hover {
    text-decoration: none;
    color: ${({ hoverColor }) => hoverColor} !important;
    border-color: ${({ hoverColor }) => hoverColor};
  }
`;

export const SearchBarWrapper = styled.div<{ searchVisible?: boolean }>`
  margin-left: 15px;
  .fa-search,
  .fa-times {
    color: #fff;
    font-size: 1rem;
    cursor: pointer;
    display: none;
  }
  ${media.mobile} {
    .fa-search {
      display: inline-block;
    }
    ${({ searchVisible }) =>
      searchVisible &&
      `
        .fa-times {
          display: inline-block;
          z-index: 30;
          color: #000;
        }
        .fa-search {
          display: none;
        }
      `}
    .academy-search-bar-wrapper {
      display: none;
      position: absolute;
      top: 30px;
      left: -12px;
      width: calc(100% + 24px);
      z-index: 20;
      .dropdown-enabled-search-bar {
        background-color: #fff;
        height: 50px;
        border-radius: 25px;
        width: calc(100% - 65px);
        .search-bar {
          input {
            height: 48px;
          }
        }
        .search-bar-dropdown {
          width: calc(100% + 55px);
          top: 58px;
          left: 0;
          max-height: calc(100vh - 100px);
        }
      }
      ${({ searchVisible }) => searchVisible && 'display: block;'}
    }
  }
`;
